import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Profile from "./profile/profile";
import Subscription from "./subscription/subscription"; // ⚠ adapte le chemin si ton dossier est différent
import Message from "./messages/messages";
import Notification from "./notification/notification";
import useUserUnreadMessages from "./messages/useUserUnreadMessages"; // adapte le chemin si besoin
import { PopupContext } from "../../PopupContext";
import "./sidebar.css";
import { ArrowLeft } from "lucide-react";

const Sidebar = () => {
  const navigate = useNavigate();
  const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
  const [isSubscribePopupVisible, setSubscribePopupVisible] = useState(false);
  const [isMessagesPopupVisible, setMessagesPopupVisible] = useState(false);
  const [isNotificationPopupVisible, setNotificationPopupVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isProfileVerified, setIsProfileVerified] = useState(false);
  /* Messages de l'admin non lus (badge de l'icône Messages) */
  const [unreadMessages, setUnreadMessages, refreshUnreadMessages] =
    useUserUnreadMessages(15000);

  const API_URL = process.env.REACT_APP_API_URL;
  const { openPackParent, popupRef, closeAllPopups } = useContext(PopupContext);
  const logoRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    navigate("/login");
  }, [navigate]);

  /* ============================================================
     Compteur de notifications non lues (rafraîchi toutes les 15 s)
     ============================================================ */
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const response = await axios.get(
        `${API_URL}/notificationsUser/unread-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching unread notifications count:", error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  }, [API_URL, handleLogout]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  /* ---------- Messages : marqués comme lus dès que le popup est ouvert ----------
     Se redéclenche aussi si un nouveau message de l'admin arrive pendant que le popup est ouvert. */
  useEffect(() => {
    if (!isMessagesPopupVisible || unreadMessages === 0) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .put(`${API_URL}/messages/user/conversation/read`, null, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => setUnreadMessages(0))
      .catch((err) => console.error("Mark messages as read error:", err));
  }, [isMessagesPopupVisible, unreadMessages, API_URL, setUnreadMessages]);

  /* ---------- Popups ---------- */
  const toggleProfilePopup = () => {
    closeAllPopups();
    setProfilePopupVisible(!isProfilePopupVisible);
  };

  const toggleSubscribePopup = () => {
    closeAllPopups();
    setSubscribePopupVisible(!isSubscribePopupVisible);
  };

  const toggleMessagesPopup = () => {
    closeAllPopups();
    // À la fermeture, on resynchronise le badge avec le serveur.
    if (isMessagesPopupVisible) refreshUnreadMessages();
    setMessagesPopupVisible(!isMessagesPopupVisible);
  };

  const toggleNotificationPopup = () => {
    closeAllPopups();
    if (isNotificationPopupVisible) fetchUnreadCount();
    setNotificationPopupVisible(!isNotificationPopupVisible);
  };

  /* ---------- Vérification du profil (animation du logo) ---------- */
  const checkProfileVerification = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const response = await axios.get(
        `${API_URL}/notificationsUser/profile-verified-notification`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data) {
        setIsProfileVerified(true);
      }
    } catch (error) {
      console.error("Error checking PROFILE_VERIFIED notification:", error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  };

  useEffect(() => {
    checkProfileVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const logo = logoRef.current;
    let animationFrameId;

    const rotateLogo = () => {
      if (!logo) return;
      if (!isProfileVerified) {
        const rotationDegree = (logo.style.transform.match(/\d+/) || [0])[0];
        logo.style.transform = `rotate(${Number(rotationDegree) + 1}deg)`;
        animationFrameId = requestAnimationFrame(rotateLogo);
      } else {
        logo.style.transform = `rotate(0deg)`;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      }
    };

    rotateLogo();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isProfileVerified]);

  /* ---------- Rendu d'un popup ---------- */
  const renderPopup = (onToggle, content) => (
    <div className="sidebar-with-popup">
      <div className="popup-header">
        <ArrowLeft
          className="sidebar-back-arrow"
          size={24}
          color="#fff"
          onClick={onToggle}
        />
      </div>
      <div className="overlayUB" onClick={() => openPackParent(null)}>
        <div
          ref={popupRef}
          className="PopupUnlimitedBonus-content"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="sidebarclient">
        <div className="sidebar-overlayclient"></div>
        <div className="logos-container-navbarclient">
          <div className="logo-itemclient" onClick={toggleProfilePopup}>
            <img
              src="/images/sidebar/ProfileU.png"
              alt="Profile"
              className="logoclient"
              ref={logoRef}
            />
          </div>

          <div className="logo-itemclient" onClick={toggleSubscribePopup}>
            <img
              src="/images/sidebar/abonnesU.png"
              alt="Subscription"
              className="logoclient"
            />
          </div>

          {/* Messages + badge des messages non lus */}
          <div className="logo-itemclient" onClick={toggleMessagesPopup}>
            <img
              src="/images/sidebar/messageU.png"
              alt="Messages"
              className="logoclient"
            />
            {unreadMessages > 0 && (
              <span className="notification-badgeclient">
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </span>
            )}
          </div>

          <div className="logo-itemclient" onClick={toggleNotificationPopup}>
            <img
              src="/images/sidebar/notificationUs.png"
              alt="Notifications"
              className="logoclient"
            />
            {unreadCount > 0 && (
              <span className="notification-badgeclient">{unreadCount}</span>
            )}
          </div>
          <div className="logo-itemclient">
            <img
              src="/images/sidebar/favoriteU.png"
              alt="Favorites"
              className="logoclient"
            />
          </div>
          <div className="logo-itemclient">
            <img
              src="/images/sidebar/settingsU.png"
              alt="Settings"
              className="logoclient"
            />
          </div>
          <div className="logo-itemclient" onClick={handleLogout}>
            <img
              src="/images/sidebar/logoutU.png"
              alt="Logout"
              className="logoclient"
            />
          </div>
        </div>
      </div>

      {createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            overflow: "visible",
            zIndex: 1000,
          }}
        >
          {isProfilePopupVisible &&
            renderPopup(
              toggleProfilePopup,
              <Profile onClose={toggleProfilePopup} />
            )}

          {isSubscribePopupVisible &&
            renderPopup(
              toggleSubscribePopup,
              <Subscription onClose={toggleSubscribePopup} />
            )}

          {isMessagesPopupVisible &&
            renderPopup(
              toggleMessagesPopup,
              <Message
                onClose={toggleMessagesPopup}
                onUnreadChange={setUnreadMessages}
              />
            )}

          {isNotificationPopupVisible &&
            renderPopup(
              toggleNotificationPopup,
              <Notification
                onClose={toggleNotificationPopup}
                onUnreadChange={setUnreadCount}
              />
            )}
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;